import type { FlowEdge, FlowNode } from '../types/flow-types';
import { checksumPortableFlow } from './flowToken';
import type {
	FlowImportMapping,
	FlowResourceKind,
	FlowResourceRequirement,
	PortableFlowMetadata,
	PortableFlowPackage,
} from './types';

const RESOURCE_MARKER = '@flow-resource:';
const SENSITIVE_KEY = /(?:auth|password|secret|token|accountId|userId)/i;

type RequirementDraft = FlowResourceRequirement & { originalValue: string };

export interface PortableFlowInput {
	nodes: FlowNode[];
	edges: FlowEdge[];
	metadata: PortableFlowMetadata;
	appVersion?: string;
}

function resourceKindFor(key: string, parentKey?: string): FlowResourceKind | null {
	if (key === 'id' && parentKey === 'device') return 'device';
	if (['deviceId', 'deviceRealId', 'sourceDeviceId'].includes(key)) return 'device';
	if (key === 'sceneId') return 'scene';
	if (key === 'groupId') return 'group';
	if (key === 'alarmId') return 'alarm';
	if (key === 'scriptId') return 'script';
	return null;
}

function genericLabel(kind: FlowResourceKind): string {
	return `Required ${kind}`;
}

function requirementId(kind: FlowResourceKind, index: number): string {
	return `${kind}-${index + 1}`;
}

export function createPortableFlowPackage(input: PortableFlowInput): PortableFlowPackage {
	const requirements: RequirementDraft[] = [];
	const byResource = new Map<string, RequirementDraft>();
	const byNodeAndKind = new Map<string, RequirementDraft>();
	const warnings = new Set<string>();

	const ensureRequirement = (
		kind: FlowResourceKind,
		originalValue: string,
		node: FlowNode,
		label?: string,
	): RequirementDraft => {
		const key = `${kind}:${originalValue}`;
		let requirement = byResource.get(key);
		const meaningfulLabel = label && label !== genericLabel(kind) ? label.trim() : '';
		if (!requirement && meaningfulLabel) {
			requirement = requirements.find(
				(item) =>
					item.kind === kind &&
					item.label.toLocaleLowerCase() === meaningfulLabel.toLocaleLowerCase(),
			);
		}
		const nodeKindKey = `${node.id}:${kind}`;
		if (!requirement) requirement = byNodeAndKind.get(nodeKindKey);
		if (!requirement) {
			requirement = {
				id: requirementId(kind, requirements.filter((item) => item.kind === kind).length),
				kind,
				label: meaningfulLabel || genericLabel(kind),
				usedByNodeIds: [],
				originalValue,
			};
			requirements.push(requirement);
		} else if (meaningfulLabel && requirement.label === genericLabel(kind)) {
			requirement.label = meaningfulLabel;
		}
		byResource.set(key, requirement);
		byNodeAndKind.set(nodeKindKey, requirement);
		if (!requirement.usedByNodeIds.includes(node.id)) requirement.usedByNodeIds.push(node.id);
		return requirement;
	};

	const sanitize = (value: unknown, node: FlowNode, key = '', parentKey = ''): unknown => {
		if (SENSITIVE_KEY.test(key)) return undefined;
		if (value === null || value === undefined) return value;
		if (Array.isArray(value)) {
			return value
				.map((entry) => sanitize(entry, node, key, parentKey))
				.filter((entry) => entry !== undefined);
		}
		if (typeof value === 'object') {
			const source = value as Record<string, unknown>;
			return Object.fromEntries(
				Object.entries(source)
					.map(([childKey, child]) => [childKey, sanitize(child, node, childKey, key)])
					.filter(([, child]) => child !== undefined),
			);
		}

		const kind = resourceKindFor(key, parentKey);
		if (kind && (typeof value === 'string' || typeof value === 'number')) {
			const original = String(value);
			const deviceLabel =
				kind === 'device'
					? String(
							(node.data.device as { name?: string } | undefined)?.name ||
								node.data.deviceName ||
								node.data.device_name ||
								(node.data._device_condition as any)?.device?.name ||
								(node.data.sceneCapability as any)?.deviceName ||
								'',
						)
					: '';
			const requirement = ensureRequirement(kind, original, node, deviceLabel);
			return `${RESOURCE_MARKER}${requirement.id}`;
		}

		if (typeof value === 'string') {
			const replaced = value.replace(/\bdev:([^:\s]+):/g, (_match, deviceId: string) => {
				const requirement = ensureRequirement(
					'device',
					deviceId,
					node,
					String((node.data.device as { name?: string } | undefined)?.name || ''),
				);
				return `dev:${RESOURCE_MARKER}${requirement.id}:`;
			});
			if (/https?:\/\//i.test(value)) warnings.add('Contains URLs; review them before importing');
			return replaced;
		}
		return value;
	};

	const nodes = input.nodes.map((node) => {
		if (node.type === 'note' && String(node.data.noteText || '').trim()) {
			warnings.add('Contains Sticky Note text that may be private');
		}
		if (node.type === 'scriptAction') warnings.add('Contains script references');
		return {
			...node,
			position: { ...node.position },
			data: sanitize(node.data, node) as FlowNode['data'],
			errors: [],
			validated: false,
		};
	});
	const edges = input.edges.map((edge) => ({ ...edge }));
	const requirementRows: FlowResourceRequirement[] = requirements.map(
		({ originalValue: _originalValue, ...requirement }) => requirement,
	);
	const unsigned = {
		format: 'shelly-flow' as const,
		formatVersion: 1 as const,
		appVersion: input.appVersion,
		exportedAt: Date.now(),
		metadata: { ...input.metadata },
		nodes,
		edges,
		requirements: requirementRows,
		warnings: [...warnings],
	};
	return { ...unsigned, checksum: checksumPortableFlow(unsigned) };
}

function materializeValue(value: unknown, mapping: FlowImportMapping): unknown {
	if (Array.isArray(value)) return value.map((entry) => materializeValue(entry, mapping));
	if (value && typeof value === 'object') {
		return Object.fromEntries(
			Object.entries(value as Record<string, unknown>).map(([key, child]) => [
				key,
				materializeValue(child, mapping),
			]),
		);
	}
	if (typeof value !== 'string') return value;
	let unresolved = false;
	const materialized = value.replace(/@flow-resource:([A-Za-z0-9-]+)/g, (_marker, id: string) => {
		const mapped = String(mapping[id] || '').trim();
		if (!mapped) unresolved = true;
		return mapped;
	});
	return unresolved ? '' : materialized;
}

export function materializePortableFlow(
	flow: PortableFlowPackage,
	mapping: FlowImportMapping,
): { nodes: FlowNode[]; edges: FlowEdge[]; unresolvedRequirementIds: string[] } {
	const unresolvedRequirementIds = flow.requirements
		.filter((requirement) => !String(mapping[requirement.id] || '').trim())
		.map((requirement) => requirement.id);
	const unresolved = new Set(unresolvedRequirementIds);
	const nodes = flow.nodes.map((node) => {
		const nodeUnresolved = flow.requirements.some(
			(requirement) =>
				unresolved.has(requirement.id) && requirement.usedByNodeIds.includes(node.id),
		);
		let data = materializeValue(node.data, mapping) as FlowNode['data'];
		if (nodeUnresolved) {
			data = {
				...data,
				sceneData: undefined,
				device: undefined,
				deviceId: '',
				deviceRealId: '',
				_device_condition: undefined,
				sceneCapability: undefined,
				sceneId: undefined,
				groupId: undefined,
				alarmId: undefined,
				scriptId: undefined,
				isValid: false,
				isConfigured: false,
				...(node.type === 'action'
					? {
							actions: [],
							label: 'Device action',
							displayLabel: 'Device action',
						}
					: {}),
			};
		}
		return {
			...node,
			position: { ...node.position },
			data,
			validated: !nodeUnresolved,
			errors: nodeUnresolved ? ['Map required resources before saving'] : [],
		};
	});
	return {
		nodes,
		edges: flow.edges.map((edge) => ({ ...edge })),
		unresolvedRequirementIds,
	};
}
